import {
  useUpdatePlaidAccessTokenMutation,
  type FullPlaidAccessTokenFragment,
} from '@transactions/graphql';
import * as React from 'react';
import {Button} from 'src/Button';
import {Modal} from 'src/Modal';
import type {SystemProps} from 'src/Modals';

export type EditPlaidTokenModalUserProps = {
  token: FullPlaidAccessTokenFragment;
  onSuccess: () => void;
};

export const EditPlaidTokenModal = ({
  onRequestClose,
  token,
  onSuccess,
}: EditPlaidTokenModalUserProps & SystemProps) => {
  const formId = React.useId();
  const [active, setActive] = React.useState(token.active);
  const [error, setError] = React.useState<string | null>(null);

  const [updateToken, {loading}] = useUpdatePlaidAccessTokenMutation();

  const hasChanges = active !== token.active;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await updateToken({
        variables: {
          id: token._id,
          active,
        },
      });
      onSuccess();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  const accountNames =
    token.accounts.map((a) => a.name).join(', ') || 'No accounts';

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={loading || !hasChanges}
      >
        {loading ? 'Saving...' : 'Save'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Edit Plaid Token"
      footer={footer}
      size="sm"
    >
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-muted-foreground text-sm">Token</label>
          <span className="font-mono text-sm">•••{token.tokenSuffix}</span>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-muted-foreground text-sm">
            Connected Accounts
          </label>
          <span className="text-sm">{accountNames}</span>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="edit-active"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="h-4 w-4"
          />
          <label htmlFor="edit-active" className="text-sm">
            Active
          </label>
        </div>
        <p className="text-muted-foreground text-xs">
          Inactive tokens will not be used for syncing transactions.
        </p>
      </form>
    </Modal>
  );
};
